import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail, RefreshCw, Loader2 } from 'lucide-react';
import logger from '../../utils/logger';
import CampaignDetailDrawer, {
  StatusPill,
  ProgressBar,
  formatDateTime,
  authHeaders,
} from './CampaignDetailDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

// ─────────────────────────────────────────────
// Main list view
// ─────────────────────────────────────────────
export const CampaignsHistory = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [quota, setQuota] = useState(null);
  const hasInitialLoad = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      const [r, qr] = await Promise.all([
        fetch(`${API_URL}/api/admin/campaigns?${params}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/admin/campaigns/quota`, { headers: authHeaders() }),
      ]);
      if (!r.ok) throw new Error(`Failed to load campaigns (${r.status})`);
      const data = await r.json();
      setItems(data.campaigns || []);
      setTotal(data.total || 0);
      if (qr.ok) setQuota(await qr.json());
    } catch (e) {
      setError(e.message || 'Failed to load campaigns');
      logger.error('[CampaignsHistory] list error', e);
    } finally {
      setLoading(false);
      hasInitialLoad.current = true;
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh list if any active campaign is in flight
  useEffect(() => {
    const hasActive = items.some((c) => c.status === 'queued' || c.status === 'sending');
    if (!hasActive) return;
    const id = setInterval(() => load(), 10000);
    return () => clearInterval(id);
  }, [items, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-700" data-testid="campaigns-history-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-green-400" />
            Campaigns
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">History of follow-up emails sent to filtered user segments.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {quota && (
            <div className="text-[11px] text-gray-400 bg-gray-900/60 px-2 py-1 rounded border border-gray-700" data-testid="campaigns-quota-badge">
              Quota: <span className="text-white">{quota.daily_used}/{quota.daily_cap}</span> today ·
              <span className="text-white"> {quota.monthly_used.toLocaleString()}/{quota.monthly_cap.toLocaleString()}</span> this month
            </div>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-sm rounded-lg flex items-center gap-1.5"
            data-testid="campaigns-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm mb-3">{error}</div>
      )}

      {loading && !hasInitialLoad.current ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-green-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12" data-testid="campaigns-empty">
          <Mail className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400">No campaigns yet</p>
          <p className="text-xs text-gray-500 mt-1">Send your first one from the Users tab → Email this segment.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="w-full text-sm" data-testid="campaigns-list-table">
              <thead className="bg-gray-900 text-gray-400">
                <tr>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Subject</th>
                  <th className="text-left py-2 px-3">From</th>
                  <th className="text-left py-2 px-3">By</th>
                  <th className="text-right py-2 px-3">Audience</th>
                  <th className="text-right py-2 px-3">Progress</th>
                  <th className="text-left py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.campaign_id}
                    onClick={() => setSelectedId(c.campaign_id)}
                    className="border-t border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                    data-testid={`campaigns-row-${c.campaign_id}`}
                  >
                    <td className="py-2 px-3 text-gray-300 whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                    <td className="py-2 px-3 text-white max-w-[24rem] truncate" title={c.subject}>{c.subject}</td>
                    <td className="py-2 px-3 text-gray-400 whitespace-nowrap">{c.sender_persona}@</td>
                    <td className="py-2 px-3 text-gray-400 whitespace-nowrap">{c.created_by_name || '—'}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{(c.audience_count || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 min-w-40">
                      <ProgressBar sent={c.sent_count || 0} failed={c.failed_count || 0} total={c.audience_count || 0} />
                    </td>
                    <td className="py-2 px-3"><StatusPill status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-2 py-1 bg-gray-700 disabled:opacity-40 text-white rounded"
                data-testid="campaigns-prev"
              >Prev</button>
              <span className="text-gray-400">Page {page + 1} of {pageCount} · {total.toLocaleString()} total</span>
              <button
                onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
                disabled={page >= pageCount - 1}
                className="px-2 py-1 bg-gray-700 disabled:opacity-40 text-white rounded"
                data-testid="campaigns-next"
              >Next</button>
            </div>
          )}
        </>
      )}

      {selectedId && (
        <CampaignDetailDrawer
          campaignId={selectedId}
          onClose={() => setSelectedId(null)}
          onCancelled={load}
        />
      )}
    </div>
  );
};
